import React, { useState } from 'react';
import { cn } from '../../lib/cn';
import { Icon } from '../Icon/Icon';
import { Button } from '../Button/Button';
import { PeriodSelector } from '../PeriodSelector/PeriodSelector';
import type { PeriodValue } from '../../lib/period';
import { ArInsurerDrawer } from '../ArCollections/ArInsurerDrawer';
import {
  AR_LATEST,
  AR_NUMBERS,
  AR_PAID_AFTER,
  AR_TODAY,
  arByFollowUp,
  arFollowUpStore,
} from '../../ar/mockData';

export interface ArAgentViewProps {
  onHold: boolean;
  /** The report period (spec 41) — the selector sits first in the header. */
  period?: PeriodValue;
  onPeriodChange?: (value: PeriodValue) => void;
  onSetHold?: (onHold: boolean) => void;
  onOpenCollections?: () => void;
  onOpenSettings?: () => void;
  onOpenTriage?: () => void;
  onOpenFollowUps?: () => void;
  onOpenCommLog?: () => void;
  /** Opens the Calls section filtered to Balance (spec 31 §7). */
  onOpenCalls?: () => void;
}

const SEG_COLORS = ['var(--ai-3)', 'var(--gold)'];

/**
 * AI Team › Accounts receivable — the read-only agent page (amendment 28 §3,
 * board `AR7Agent`), in the same frame as Treatment Coordinator.
 *
 * Every number reads `AR_NUMBERS` and the follow-up store, so it matches
 * Collections and the Dashboard. The funnel lives on Collections › Balances, so
 * the hero here is **what was collected**, split link vs desk/phone. AI calls
 * stay — the practice is calling its own patients, who agreed to calls. Hold
 * works as Recall's.
 */
export function ArAgentView({ onHold, period, onPeriodChange, onSetHold, onOpenCollections, onOpenSettings, onOpenTriage, onOpenFollowUps, onOpenCommLog, onOpenCalls }: ArAgentViewProps) {
  const [asking, setAsking] = useState(false);
  const [seeWho, setSeeWho] = useState(false);
  const n = AR_NUMBERS;
  const active = arFollowUpStore.followUps.filter((f) => f.state === 'active').length;
  const byFollowUp = arByFollowUp();
  const linkAmt = Number(n.throughLink.replace(/[^0-9]/g, ''));
  const deskAmt = Number(n.atDeskOrPhone.replace(/[^0-9]/g, ''));
  const total = linkAmt + deskAmt;

  return (
    <div className="cal-fade mx-auto min-h-0 w-full max-w-[1084px] flex-1 overflow-y-auto px-[30px] pb-16 pt-5">
      {/* header */}
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div className="min-w-0">
          <div className="pc-eyebrow text-[var(--ink-3)]">{active} follow-ups active · {onHold ? 'on hold' : 'working'}</div>
          <h1 className="m-0 mt-0.5 font-[var(--font-display)] text-[24px] font-semibold tracking-[-0.02em] text-[var(--ink-0)]">
            Accounts <span className="text-[var(--ai-3)]">receivable</span>
          </h1>
          <p className="m-0 mt-0.5 text-[12.5px] text-[var(--ink-3)]">Text, email and AI call · Alamo Heights Dental (San Antonio, TX)</p>
        </div>
        <div className="flex items-center gap-2">
          {period && onPeriodChange ? (
            <PeriodSelector variant="agent" value={period} onChange={onPeriodChange} startedISO="2026-03-03" />
          ) : null}
          <Button variant="primary" size="sm" iconLeft="credit" onClick={onOpenCollections}>Open Collections</Button>
          <Button variant="secondary" size="sm" iconLeft="cog" onClick={onOpenSettings}>Settings</Button>
          {onHold ? (
            <Button variant="primary" size="sm" iconLeft="play" onClick={() => { onSetHold?.(false); }}>Resume</Button>
          ) : (
            <Button variant="danger" size="sm" iconLeft="pause" onClick={() => { setAsking(true); }}>Hold</Button>
          )}
        </div>
      </div>

      {onHold ? (
        <section className="mt-4 flex flex-wrap items-center gap-3 rounded-[var(--r-lg)] border border-[color-mix(in_srgb,var(--terracotta)_35%,transparent)] bg-[var(--cream-0)] px-4 py-2.5">
          <Icon kind="pause" size={15} stroke={2} style={{ color: 'var(--terracotta)' }} />
          <span className="text-[13px] font-semibold text-[var(--terracotta)]">Accounts receivable is on hold</span>
          <span className="text-[12px] text-[var(--ink-2)]">No A/R message or call goes out until you resume.</span>
        </section>
      ) : null}

      {/* Calls strip (spec 31 §7): what was a mixed "3 need a person" Triage alert
          splits — the phone calls go to Calls, replies and plan requests to Triage. */}
      <section className="mt-4 flex flex-wrap items-center gap-3 rounded-[var(--r-lg)] border border-[color-mix(in_srgb,var(--gold)_35%,transparent)] bg-[var(--gold-tint)] px-4 py-2.5">
        <Icon kind="phone-out" size={15} stroke={2} style={{ color: 'var(--gold)' }} />
        <span className="text-[13px] font-semibold text-[var(--gold)]">8 to call</span>
        <span className="text-[12px] text-[var(--ink-2)]">6 over $1,000 · 1 from the person-call step · 1 asked to be called</span>
        <button type="button" className="ml-auto text-[12.5px] font-semibold text-[var(--gold)] hover:underline" onClick={onOpenCalls}>Open in Calls →</button>
      </section>
      <div className="mt-2 text-[11.5px] text-[var(--ink-3)]">
        Replies about a bill and payment-plan requests stay in <button type="button" className="font-semibold text-[var(--gold)] hover:underline" onClick={onOpenTriage}>Triage</button>.
      </div>

      {/* today */}
      <div className="mt-4 flex flex-wrap gap-x-8 gap-y-2 rounded-[var(--r-lg)] border border-[var(--cream-2)] bg-[var(--cream-1)] px-5 py-3.5 shadow-[var(--sh-1)]">
        <div className="text-[10.5px] font-bold uppercase tracking-[0.09em] text-[var(--ink-4)]">Today</div>
        {AR_TODAY.map((p) => (
          <span key={p.label} className="inline-flex items-baseline gap-1.5">
            <span className="font-[var(--font-mono)] text-[16px] font-bold text-[var(--ink-0)]">{p.value}</span>
            <span className="text-[12px] text-[var(--ink-3)]">{p.label}</span>
          </span>
        ))}
      </div>

      {/* hero — collected */}
      <div className="mt-5 rounded-[var(--r-lg)] border border-l-[3px] border-[var(--cream-2)] border-l-[var(--brand-indigo)] bg-[var(--cream-1)] p-4 shadow-[var(--sh-1)]">
        <div className="pc-eyebrow text-[var(--ink-3)]">Collected</div>
        <div className="mt-1 flex items-baseline gap-2">
          <span className="font-[var(--font-display)] text-[32px] font-semibold tracking-[-0.03em] text-[var(--ink-0)]">{n.collected30}</span>
          <span className="text-[13px] text-[var(--ink-3)]">from {n.collectedPatients} patients · {n.inFollowUp} followed up</span>
        </div>
        <div className="mt-3 flex h-3 overflow-hidden rounded-full">
          <div style={{ width: `${String((linkAmt / total) * 100)}%`, background: SEG_COLORS[0] }} title={`Through the payment link: ${n.throughLink}`} />
          <div style={{ width: `${String((deskAmt / total) * 100)}%`, background: SEG_COLORS[1] }} title={`At the desk or by phone: ${n.atDeskOrPhone}`} />
        </div>
        <div className="mt-2.5 flex flex-wrap gap-x-5 gap-y-1.5">
          <span className="inline-flex items-center gap-1.5 text-[11.5px]"><span className="h-2 w-2 rounded-[2px]" style={{ background: SEG_COLORS[0] }} /><span className="text-[var(--ink-3)]">Through the payment link</span><span className="font-[var(--font-mono)] font-bold text-[var(--ink-0)]">{n.throughLink}</span></span>
          <span className="inline-flex items-center gap-1.5 text-[11.5px]"><span className="h-2 w-2 rounded-[2px]" style={{ background: SEG_COLORS[1] }} /><span className="text-[var(--ink-3)]">At the desk or by phone</span><span className="font-[var(--font-mono)] font-bold text-[var(--ink-0)]">{n.atDeskOrPhone}</span></span>
        </div>
        <div className="mt-3 border-t border-[var(--cream-2)] pt-2 text-[12px] text-[var(--ink-4)]">
          Payments made within 30 days of a message or call from a follow-up. Amounts from ELVA Pay and the PMS ledger.
        </div>
      </div>

      {/* tiles */}
      <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-3">
        <Tile label="Median days to pay" value={String(n.medianDaysToPay)} sub="from the first message" />
        <Tile label="With the insurer" value={n.withInsurer} sub={`${String(n.withInsurerPatients)} patients · ELVA doesn’t ask them`} onSeeWho={() => { setSeeWho(true); }} />
        <Tile label="Disputes found" value={String(n.disputesFound)} sub="sent to a person · 1 was a billing error" />
      </div>

      {/* 01 by follow-up */}
      <Card className="mt-3">
        <div className="flex items-start justify-between">
          <SectionHeading n="01" title="By follow-up" sub="" />
          <button type="button" className="text-[12px] font-semibold text-[var(--ai-3)] hover:underline" onClick={onOpenFollowUps}>Follow-ups →</button>
        </div>
        <div className="grid grid-cols-[1.8fr_0.7fr_0.6fr_0.8fr] gap-x-3 px-1 pb-1 font-[var(--font-mono)] text-[9.5px] font-bold uppercase tracking-[0.07em] text-[var(--ink-4)]">
          <span>Follow-up</span><span className="text-right">In it now</span><span className="text-right">Paid</span><span className="text-right">Collected</span>
        </div>
        {byFollowUp.map((r) => (
          <div key={r.name} className="grid grid-cols-[1.8fr_0.7fr_0.6fr_0.8fr] items-center gap-x-3 border-t border-[var(--cream-2)] px-1 py-2 text-[12.5px]">
            <span className="font-semibold text-[var(--ink-0)]">{r.name}</span>
            <span className="text-right font-[var(--font-mono)] text-[var(--ink-2)]">{r.inProgress}</span>
            <span className="text-right font-[var(--font-mono)] text-[var(--ink-2)]">{r.paid}</span>
            <span className="text-right font-[var(--font-mono)] font-bold text-[var(--ink-0)]">{r.collected}</span>
          </div>
        ))}
        <div className="grid grid-cols-[1.8fr_0.7fr_0.6fr_0.8fr] items-center gap-x-3 border-t-2 border-[var(--cream-2)] px-1 py-2 text-[12.5px] font-bold">
          <span>Total</span>
          <span className="text-right font-[var(--font-mono)]">{n.inFollowUp}</span>
          <span className="text-right font-[var(--font-mono)]">{n.collectedPatients}</span>
          <span className="text-right font-[var(--font-mono)]">{n.collected30}</span>
        </div>
      </Card>

      {/* 02 what they paid after */}
      <Card className="mt-3">
        <SectionHeading n="02" title="What they paid after" sub="The last thing before they paid" />
        <div className="grid grid-cols-[1.8fr_0.6fr] gap-x-3 px-1 pb-1 font-[var(--font-mono)] text-[9.5px] font-bold uppercase tracking-[0.07em] text-[var(--ink-4)]">
          <span /><span className="text-right">Patients</span>
        </div>
        {AR_PAID_AFTER.map((r) => (
          <div key={r.label} className="grid grid-cols-[1.8fr_0.6fr] items-center gap-x-3 border-t border-[var(--cream-2)] px-1 py-2 text-[12.5px]">
            <span className="text-[var(--ink-2)]">{r.label}</span>
            <span className="text-right font-[var(--font-mono)] font-bold text-[var(--ink-0)]">{r.patients}</span>
          </div>
        ))}
        <div className="grid grid-cols-[1.8fr_0.6fr] items-center gap-x-3 border-t-2 border-[var(--cream-2)] px-1 py-2 text-[12.5px] font-bold">
          <span>Total</span>
          <span className="text-right font-[var(--font-mono)]">{n.collectedPatients}</span>
        </div>
      </Card>

      {/* 03 latest */}
      <Card className="mt-3">
        <div className="flex items-start justify-between">
          <SectionHeading n="03" title="Latest" sub="Including what ELVA chose not to do." />
          <button type="button" className="text-[12px] font-semibold text-[var(--ai-3)] hover:underline" onClick={onOpenCommLog}>Communication log →</button>
        </div>
        <div className="flex flex-col">
          {AR_LATEST.map((it, i) => (
            <div key={`${it.at}-${String(i)}`} className="flex items-baseline justify-between gap-3 border-t border-[var(--cream-2)] py-2 first:border-0 text-[12.5px]">
              <span className="text-[var(--ink-2)]">{it.body}</span>
              <span className="shrink-0 font-[var(--font-mono)] text-[10.5px] text-[var(--ink-4)]">{it.at}</span>
            </div>
          ))}
        </div>
      </Card>

      <ArInsurerDrawer open={seeWho} onClose={() => { setSeeWho(false); }} />

      {asking ? (
        <HoldModal onCancel={() => { setAsking(false); }} onConfirm={() => { setAsking(false); onSetHold?.(true); }} />
      ) : null}
    </div>
  );
}

function Tile({ label, value, sub, onSeeWho }: { label: string; value: string; sub: string; onSeeWho?: () => void }) {
  return (
    <div className="rounded-[var(--r-lg)] border border-[var(--cream-2)] bg-[var(--cream-1)] p-4 shadow-[var(--sh-1)]">
      <div className="pc-eyebrow text-[var(--ink-3)]">{label}</div>
      <div className="mt-1 font-[var(--font-display)] text-2xl font-semibold tracking-[-0.03em] text-[var(--ink-0)]">{value}</div>
      <div className="mt-0.5 text-[11.5px] leading-[1.45] text-[var(--ink-4)]">{sub}</div>
      {onSeeWho !== undefined ? (
        <button type="button" onClick={onSeeWho} className="mt-1 text-[11.5px] font-semibold text-[var(--ai-3)] hover:underline">See who →</button>
      ) : null}
    </div>
  );
}

function Card({ children, className }: { children: React.ReactNode; className?: string }) {
  return <div className={cn('rounded-[var(--r-lg)] border border-[var(--cream-2)] bg-[var(--cream-1)] p-4 shadow-[var(--sh-1)]', className)}>{children}</div>;
}

function SectionHeading({ n, title, sub }: { n: string; title: string; sub: string }) {
  return (
    <div className="mb-2.5 flex flex-wrap items-baseline gap-2.5">
      <span className="font-[var(--font-mono)] text-[11px] font-bold text-[var(--ink-4)]">{n}</span>
      <h2 className="m-0 text-[14px] font-bold text-[var(--ink-0)]">{title}</h2>
      <span className="text-[12px] text-[var(--ink-4)]">{sub}</span>
    </div>
  );
}

function HoldModal({ onCancel, onConfirm }: { onCancel: () => void; onConfirm: () => void }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-[rgba(20,22,28,0.35)] px-4" role="dialog" aria-modal="true" aria-labelledby="hold-ar-title">
      <div className="w-full max-w-[420px] rounded-[var(--r-lg)] border border-[var(--cream-2)] bg-[var(--cream-1)] p-5 shadow-[var(--sh-3)]">
        <h2 id="hold-ar-title" className="m-0 text-[16px] font-bold text-[var(--ink-0)]">Hold Accounts receivable?</h2>
        <p className="m-0 mt-2 text-[13px] leading-[1.6] text-[var(--ink-2)]">No A/R message or call goes out until you resume. Replies are still answered, and the payment links already sent still work.</p>
        <div className="mt-4 flex justify-end gap-2">
          <Button variant="secondary" size="sm" onClick={onCancel}>Cancel</Button>
          <Button variant="danger" size="sm" onClick={onConfirm}>Hold</Button>
        </div>
      </div>
    </div>
  );
}
